import { Wallet } from 'lucide-react';
import type { AiCampaignForm } from '@/features/campaigns/types/campaign';
import { AD_DURATIONS, MIN_CAMPAIGN_AMOUNT, MAX_CAMPAIGN_AMOUNT } from '@/features/campaigns/constants/campaignOptions';
import { FieldError, FieldLabel, campInputClass } from '@/features/campaigns/components/FormPrimitives';
import { formatCompactMoney } from '@/shared/lib/format/money';
import { BUDGET_PRESETS } from '../AIBuilder.types';

type BudgetDurationSectionProps = {
  formData: AiCampaignForm;
  setFormData: React.Dispatch<React.SetStateAction<AiCampaignForm>>;
  errors: Record<string, string>;
  clearFieldError: (field: string) => void;
  currency: string;
};

export function BudgetDurationSection({
  formData,
  setFormData,
  errors,
  clearFieldError,
  currency,
}: BudgetDurationSectionProps) {
  return (
    <section className="camp-section text-left">
      <div className="mb-4 flex items-center gap-2">
        <Wallet className="h-4 w-4 text-[var(--dash-brand)]" />
        <h2 className="m-0 text-[15px] font-semibold text-[var(--dash-ink)]">
          Budget & ad length
        </h2>
      </div>

      <FieldLabel
        required
        hint={`Min ${currency} ${MIN_CAMPAIGN_AMOUNT.toLocaleString('en-IN')} · Max ${currency} ${MAX_CAMPAIGN_AMOUNT.toLocaleString('en-IN')}`}
      >
        Total budget
      </FieldLabel>
      <div className="relative mt-1.5">
        <span className="pointer-events-none absolute top-1/2 left-3.5 -translate-y-1/2 text-sm font-semibold text-[var(--dash-muted)]">
          {currency}
        </span>
        <input
          type="number"
          min={MIN_CAMPAIGN_AMOUNT}
          max={MAX_CAMPAIGN_AMOUNT}
          inputMode="numeric"
          value={formData.budget}
          onKeyDown={(e) => {
            if (e.key === '-' || e.key === 'e' || e.key === 'E' || e.key === '+') {
              e.preventDefault();
            }
          }}
          onChange={(e) => {
            clearFieldError('budget');
            const raw = e.target.value;
            if (raw === '') {
              setFormData((prev) => ({ ...prev, budget: '' }));
              return;
            }
            const amount = Number(raw);
            if (Number.isNaN(amount) || amount < 0) return;
            setFormData((prev) => ({ ...prev, budget: raw }));
          }}
          placeholder="e.g. 2500"
          aria-invalid={Boolean(errors.budget)}
          className={`${campInputClass} pl-12 ${errors.budget ? 'is-invalid' : ''}`}
        />
      </div>
      <FieldError message={errors.budget} />

      <div className="mt-3 flex flex-wrap gap-2">
        {BUDGET_PRESETS.map((amount) => {
          const active = Number(formData.budget) === amount;
          return (
            <button
              key={amount}
              type="button"
              onClick={() => {
                clearFieldError('budget');
                setFormData((prev) => ({ ...prev, budget: String(amount) }));
              }}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${active
                ? 'bg-[var(--dash-brand)] text-white'
                : 'bg-[var(--dash-soft)] text-[var(--dash-muted)] hover:text-[var(--dash-ink)]'
                }`}
            >
              {formatCompactMoney(amount)}
            </button>
          );
        })}
      </div>

      <div className="mt-5">
        <FieldLabel required hint="How long each play runs on screen">
          Ad duration
        </FieldLabel>
        <div className="mt-2 grid grid-cols-4 gap-2">
          {AD_DURATIONS.map((d) => {
            const active = formData.adDuration === d;
            return (
              <button
                key={d}
                type="button"
                onClick={() => {
                  clearFieldError('adDuration');
                  setFormData((prev) => ({ ...prev, adDuration: d }));
                }}
                className={`rounded-xl border py-2.5 text-sm font-semibold transition ${active
                  ? 'border-[var(--dash-brand)] bg-[var(--dash-brand-soft)] text-[var(--dash-brand)]'
                  : 'border-[var(--dash-line)] bg-white text-[var(--dash-muted)] hover:border-[#C5D4E6]'
                  }`}
              >
                {d}s
              </button>
            );
          })}
        </div>
        <FieldError message={errors.adDuration} />
      </div>
    </section>
  );
}
